/**
 * SectionCoachmark — Tutorial contestuale INLINE che appare la prima volta
 * che l'agente entra in una sezione chiave (Vetrina, Sopralluogo review,
 * Foto tab, ecc.).
 *
 * iter v33 r8 · User M1485 (audio): «non è chiaro cosa uno può fare, la
 * vetrina è così, sempre un po' macchinoso, deve essere immediato».
 * Risposta: un mini-tutorial di 3 step ("Cosa puoi fare qui") che appare
 * UNA SOLA VOLTA per sezione (persistente in localStorage) e sparisce con
 * la X o con "Non mostrare più".
 *
 * Design principles:
 * - INLINE (non modale) — non blocca l'accesso alla sezione, si può
 *   scorrere sotto anche senza dismissare.
 * - Card cream su sfondo bianco con eyebrow "COSA PUOI FARE QUI" · X in
 *   alto a destra.
 * - 3 step compatti con icona + micro-title + 1 riga di descrizione.
 * - "Non mostrare più" testo underline in fondo (chiude senza flash).
 * - Aperto → animazione fade-in soft, chiuso → set localStorage flag +
 *   fade-out breve.
 *
 * Uso:
 *   <SectionCoachmark
 *     id="foto-tab"                       // chiave localStorage univoca
 *     eyebrow="Cosa puoi fare qui"
 *     title="Sistema le foto in tre mosse"
 *     steps={[
 *       { icon: <Sparkle />, title: "Migliora tutte", body: "..." },
 *       ...
 *     ]}
 *   />
 */
import React, { useEffect, useState } from "react";
import { X } from "@phosphor-icons/react";

const LS_PREFIX = "pronto:coachmark:";

export default function SectionCoachmark({
  id,
  eyebrow = "Cosa puoi fare qui",
  title,
  steps = [],
  showDelayMs = 350,
}) {
  const lsKey = `${LS_PREFIX}${id}`;
  const [open, setOpen] = useState(false);
  const [dismissing, setDismissing] = useState(false);

  useEffect(() => {
    if (!id || steps.length === 0) return undefined;
    try {
      if (localStorage.getItem(lsKey) === "1") return undefined;
    } catch {
      // localStorage non disponibile (private mode) — meglio non mostrare
      // per evitare di ri-triggerare ad ogni reload.
      return undefined;
    }
    const t = setTimeout(() => setOpen(true), showDelayMs);
    return () => clearTimeout(t);
  }, [id, lsKey, steps.length, showDelayMs]);

  const dismiss = () => {
    setDismissing(true);
    // fade-out breve, poi setto localStorage + smonto
    setTimeout(() => {
      try { localStorage.setItem(lsKey, "1"); } catch { /* noop */ }
      setOpen(false);
      setDismissing(false);
    }, 220);
  };

  if (!open || steps.length === 0) return null;

  return (
    <div
      className={`relative rounded-2xl border border-[#EAE7DF] bg-[#FAF8F3] shadow-sm px-4 pt-4 pb-4 sm:px-5 sm:pt-5 sm:pb-5 mb-5 transition-all duration-200 ${
        dismissing ? "opacity-0 -translate-y-1" : "opacity-100 translate-y-0"
      }`}
      data-testid={`coachmark-${id}`}
      role="region"
      aria-label={eyebrow}
    >
      {/* Close X */}
      <button
        type="button"
        onClick={dismiss}
        className="absolute top-3 right-3 w-8 h-8 rounded-full text-[#9A968B] hover:text-[#1C1C1A] hover:bg-white inline-flex items-center justify-center transition-colors"
        aria-label="Chiudi tutorial"
        data-testid={`coachmark-${id}-close`}
      >
        <X size={13} weight="bold" />
      </button>

      {/* Header */}
      <div className="mb-3 pr-8">
        <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-[#7C8B6E] mb-1.5">
          {eyebrow}
        </div>
        {title && (
          <h3 className="font-editorial text-[18px] sm:text-[19px] leading-tight text-[#1C1C1A]">
            {title}
          </h3>
        )}
      </div>

      {/* Steps: 1 colonna mobile, 3 colonne desktop */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {steps.map((s, i) => (
          <div
            key={i}
            className="flex items-start gap-2.5 sm:flex-col sm:items-start"
            data-testid={`coachmark-${id}-step-${i + 1}`}
          >
            <div className="shrink-0 w-8 h-8 rounded-full bg-white border border-[#EAE7DF] inline-flex items-center justify-center text-[#7C8B6E]">
              {s.icon}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[12.5px] font-semibold text-[#1C1C1A] leading-tight mb-0.5 flex items-center gap-1.5">
                <span className="text-[10px] font-mono text-[#9A968B]">
                  {i + 1}.
                </span>
                {s.title}
              </div>
              <div className="text-[11.5px] text-[#6B6A65] leading-snug">
                {s.body}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Dismiss */}
      <button
        type="button"
        onClick={dismiss}
        className="mt-4 block mx-auto text-[11px] text-[#9A968B] hover:text-[#6B6A65] underline underline-offset-2"
        data-testid={`coachmark-${id}-skip`}
      >
        Non mostrare più
      </button>
    </div>
  );
}
